import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useTranslation } from '@/i18n';
import { useUI } from '@/hooks/ui/useUI';
import { usePagePermissions } from '@/hooks/authorization/usePagePermissions';
import { usePaginatedList } from '@/hooks/ui/usePaginatedList';
import type {
  PaginatedFetchParams,
  PaginatedFetchResult,
} from '@/hooks/ui/usePaginatedList';
import type { DateRangeValue } from '@/components/ui/filters/DateRangeFilter';
import { marksManagerService } from '@/api/domains/attendance/services/marksManager.service';
import type {
  LC_RecalculateMarksManagerIn,
  MarksManagerRow,
  MarksManagerDraftRow,
  MarksManagerAttendanceStatusCode,
} from '@/api/domains/attendance/types/marksManager.types';
import { addDaysToDdMmYyyy, convertDecimalToTime, formatDateToYYYYMMDD } from '@/utils';

import type { TableColumn } from '@/components/ui/tables/Table.types';
import {
  useEmployeeDimensionalFilter,
  type UseEmployeeDimensionalFilterResult,
} from '@/hooks/attendance/common/useEmployeeDimensionalFilter';
import { useHoldingCompanies } from '@/api/domains/catalog/holding/hooks/useHoldingCompanies';
import { MarksManagerJornadaCell } from '@/components/features/attendance';


// AC de ASIS-5567: rango por defecto "ayer y hoy" al cargar la pantalla.
function getDefaultDateRange(): DateRangeValue {
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  return { from: formatDateToYYYYMMDD(yesterday), to: formatDateToYYYYMMDD(today) };
}

export type QueryMode = 'rango' | 'corteMensual';
export type MarksManagerHourFormat = 'HH:MM:SS' | 'DECIMAL';

const DECIMAL_PLACES = 4;

interface FilterState {
  dateRange: DateRangeValue;
  employeeIds: number[];
  attendanceStatusCodes: MarksManagerAttendanceStatusCode[];
  companyIds: string[];
  areaIds: string[];
  positionIds: string[];
  costCenterIds: string[];
  contractModalityIds: string[];
  groupIds: string[];
  employeeStatus: string;
}

function toCsv(values: (string | number)[]): string | undefined {
  return values.length > 0 ? values.join(',') : undefined;
}

const ORDER_BY_MAP: Record<string, string> = {
  rut: 'employeeTaxIdentificationNumber',
  fullName: 'fullName',
  date: 'date',
};

// ASIS-5572: fila mostrada en la tabla — o bien una jornada real (MarksManagerRow) o una fila
// agregada localmente vía "+"/"-" en la columna Jornada (MarksManagerDraftRow), todavía sin
// contraparte en el backend.
export type MarksManagerDisplayRow = MarksManagerRow | MarksManagerDraftRow;

function anchorRowKeyOf(row: MarksManagerRow): string {
  return `${row.employeeId}-${row.date}`;
}

export interface UseMarksManagerResult {
  rows: MarksManagerDisplayRow[];
  total: number;
  page: number;
  pageSize: number;
  setPage: (page: number) => void;
  setPageSize: (size: number) => void;
  columns: TableColumn<MarksManagerDisplayRow>[];
  onSearch: (term: string) => void;
  onSortChange: (columnKey: string, direction: 'asc' | 'desc') => void;
  isLoading: boolean;
  onRefresh: () => void;
  dateRange: DateRangeValue;
  setDateRange: (range: DateRangeValue) => void;
  queryMode: QueryMode;
  setQueryMode: (mode: QueryMode) => void;
  canView: boolean;
  isLoaded: boolean;
  tableSelectionKey: string;
  isSelectAllTotalActive: boolean;
  onSelectAllTotal: () => void;
  onClearSelectionTotal: () => void;
  isDecimalFormat: boolean;
  onHourFormatToggle: (useDecimal: boolean) => void;
  dimensionalFilter: UseEmployeeDimensionalFilterResult;
  attendanceStatusCodes: MarksManagerAttendanceStatusCode[];
  setAttendanceStatusCodes: (codes: MarksManagerAttendanceStatusCode[]) => void;
  onAdvancedSearch: () => void;
  /**
   * Estado "sucio" genérico (ASIS-5571): true cuando hay modificaciones sin guardar en la tabla.
   * Sin productor todavía en esta historia (las columnas editables — horario, marcajes, jornada —
   * llegan en historias posteriores del épico ASIS-5566); queda expuesto como el punto de enganche
   * que esas historias usarán para marcar la tabla como sucia.
   */
  hasPendingChanges: boolean;
  setHasPendingChanges: (value: boolean) => void;
  isRecalculating: boolean;
  onSaveRecalculate: () => void;
}

export function useMarksManager(): UseMarksManagerResult {
  const { t: translate } = useTranslation();
  const { showApiError, showSuccess, openModal, closeModal } = useUI();
  const { canView, isLoaded } = usePagePermissions();

  const [queryMode, setQueryMode] = useState<QueryMode>('rango');

  const initialFilters = useMemo<FilterState>(() => ({
    dateRange: getDefaultDateRange(),
    employeeIds: [],
    attendanceStatusCodes: [],
    companyIds: [],
    areaIds: [],
    positionIds: [],
    costCenterIds: [],
    contractModalityIds: [],
    groupIds: [],
    employeeStatus: 'vigente',
  }), []);

  // ASIS-5571: filtros vigentes de la última búsqueda aplicada, capturados aquí (en vez de leer el
  // borrador dateRangeDraft/dimensionalFilter, que puede estar sin confirmar con el panel avanzado
  // expandido) para que "Guardar/Recalcular" actúe exactamente sobre el mismo universo mostrado en
  // la tabla.
  const lastAppliedFiltersRef = useRef<FilterState>(initialFilters);

  const fetchFn = useCallback(
    async (
      params: PaginatedFetchParams<FilterState>,
      signal: AbortSignal,
    ): Promise<PaginatedFetchResult<MarksManagerRow> | null> => {
      lastAppliedFiltersRef.current = params.filters;

      const response = await marksManagerService.list(
        {
          page: params.page,
          pageSize: params.pageSize,
          dateFrom: params.filters.dateRange.from ?? undefined,
          dateTo: params.filters.dateRange.to ?? undefined,
          searchText: params.searchText,
          orderBy: params.sort?.key,
          orderDirection: params.sort?.direction,
          employeeIds: toCsv(params.filters.employeeIds),
          attendanceStatusCodes: toCsv(params.filters.attendanceStatusCodes),
          companyIds: toCsv(params.filters.companyIds),
          areaIds: toCsv(params.filters.areaIds),
          positionIds: toCsv(params.filters.positionIds),
          costCenterIds: toCsv(params.filters.costCenterIds),
          contractModalityIds: toCsv(params.filters.contractModalityIds),
          groupIds: toCsv(params.filters.groupIds),
          employeeStatus: params.filters.employeeStatus || undefined,
        },
        signal,
      );

      if (!response.success || !response.data) {
        if (response.error?.code === 'ABORTED') return null;
        showApiError(response, translate('attendance:marksManager.messages.loadError'));
        return null;
      }

      return {
        items: response.data.items,
        totalRecords: response.data.pagination.totalRecords,
      };
    },
    [showApiError, translate],
  );

  const {
    rows,
    total,
    page,
    pageSize,
    setPage,
    setPageSize,
    loading,
    onSearch,
    onSortChange,
    setFilters,
    refetch,
  } = usePaginatedList<MarksManagerRow, FilterState>({
    fetchFn,
    initialFilters,
    orderByMap: ORDER_BY_MAP,
    initialSort: { key: 'fullName', direction: 'ASC' },
    enabled: isLoaded && canView,
  });

  // ASIS-5570: el rango de fecha pasa a ser un borrador local (dateRangeDraft) en vez de escribir
  // directo a usePaginatedList — con el panel avanzado expandido, el cambio queda en borrador
  // hasta presionar "Buscar" (handleAdvancedSearch); colapsado, sigue aplicando en caliente como
  // en ASIS-5567 (ver el efecto más abajo).
  const [dateRangeDraft, setDateRangeDraft] = useState<DateRangeValue>(getDefaultDateRange());

  // ASIS-5572: referencia estable (useCallback, no un método "suelto" de marksManagerService) —
  // useEmployeeDimensionalFilter la usa como dependencia de su propio efecto de recarga, así que
  // una identidad nueva en cada render dispararía un refetch en cada render de este hook.
  const fetchMarksManagerEmployees = useCallback(
    (params: Parameters<typeof marksManagerService.listEmployees>[0], signal?: AbortSignal) =>
      marksManagerService.listEmployees(params, signal),
    [],
  );

  // ASIS-5569: filtro rápido "Colaborador" + los 7 filtros dimensionales del panel "Ver filtros
  // avanzados" (Empresa/Área/Cargo/C.C./Agrupaciones/Modalidad de contrato/Estado colaborador).
  // Se reutiliza tal cual el hook ya usado por AttendanceMarksAdmin/MarksReport/etc. — la opción
  // "Colaborador" del picker ya viene acotada por los otros filtros dimensionales vigentes.
  const dimensionalFilter = useEmployeeDimensionalFilter(
    {
      company: true,
      area: true,
      position: true,
      costCenter: true,
      contractModality: true,
      securityGroup: true,
      employeeStatus: true,
      employee: true,
    },
    undefined,
    {
      // "Colaborador" es un filtro rápido siempre visible, fuera del panel colapsable de filtros
      // avanzados — sin esto, la carga de colaboradores nunca dispara porque el hook solo la
      // hace cuando isExpanded es true (asume que el picker vive dentro del panel avanzado).
      eagerEmployeeFetch: true,
      // ASIS-5572: usa el endpoint propio de Gestor de Marcas (readLevel de MarksManagerSections)
      // en vez de employeeFilteredService.getFiltered (readLevel de EmployeeSections) — un
      // usuario puede tener permiso amplio en esta pantalla y solo "Personal" en el módulo
      // Empleados, lo que dejaba el combo "Colaborador" mostrando únicamente al propio usuario
      // logueado sin importar el texto buscado.
      employeeSearchFn: fetchMarksManagerEmployees,
    },
  );

  // ASIS-5570: Empresa del panel avanzado se preselecciona con la empresa padre/holding del
  // usuario al montar la pantalla — no la bloquea, sigue editable (mismo patrón que
  // useOvertimeApproval.tsx).
  const { companies, loading: isLoadingCompanies } = useHoldingCompanies();
  const hasInitialisedCompanyRef = useRef(false);
  const { onCompanyChange } = dimensionalFilter.handlers;

  useEffect(() => {
    if (hasInitialisedCompanyRef.current || isLoadingCompanies || companies.length === 0) return;

    const parentCompany = companies.find((company) => company.parentHoldingCompanyId === undefined)
      ?? companies[0];

    hasInitialisedCompanyRef.current = true;
    onCompanyChange([String(parentCompany.id)]);
  }, [companies, isLoadingCompanies, onCompanyChange]);

  // ASIS-5569: filtro rápido "Estado asistencia" (catálogo cerrado de 6 valores).
  const [attendanceStatusCodes, setAttendanceStatusCodes] =
    useState<MarksManagerAttendanceStatusCode[]>([]);

  // ASIS-5570: commitea el rango de fecha (borrador) + los filtros dimensionales/rápidos vigentes
  // hacia usePaginatedList en un solo golpe — es el único punto que aplica la combinación
  // completa de filtros principales + avanzados + rápidos.
  const handleAdvancedSearch = useCallback(() => {
    setFilters((previous) => ({
      ...previous,
      dateRange: dateRangeDraft,
      employeeIds: dimensionalFilter.selectedEmployeeIds,
      attendanceStatusCodes,
      companyIds: dimensionalFilter.dimensionalFilters.companyIds,
      areaIds: dimensionalFilter.dimensionalFilters.areaIds,
      positionIds: dimensionalFilter.dimensionalFilters.positionIds,
      costCenterIds: dimensionalFilter.dimensionalFilters.costCenterIds,
      contractModalityIds: dimensionalFilter.dimensionalFilters.contractModalityIds,
      groupIds: dimensionalFilter.dimensionalFilters.groupIds,
      employeeStatus: dimensionalFilter.dimensionalFilters.status,
    }));
  }, [
    dateRangeDraft,
    dimensionalFilter.selectedEmployeeIds,
    dimensionalFilter.dimensionalFilters,
    attendanceStatusCodes,
    setFilters,
  ]);

  // Con el panel de filtros avanzados colapsado, cambiar la fecha o un filtro rápido (Colaborador,
  // Estado asistencia) sigue aplicando en caliente (AC de ASIS-5567/5569). Expandido, ningún
  // cambio se aplica hasta presionar "Buscar" (handleAdvancedSearch, expuesto como
  // onAdvancedSearch) — mismo híbrido que useAttendanceFiltersPanel. Se omite el primer render
  // para no duplicar el fetch inicial de usePaginatedList.
  const isFirstDimensionalSyncRef = useRef(true);
  useEffect(() => {
    if (isFirstDimensionalSyncRef.current) {
      isFirstDimensionalSyncRef.current = false;
      return;
    }
    if (dimensionalFilter.isExpanded) return;

    handleAdvancedSearch();
  }, [
    dateRangeDraft,
    dimensionalFilter.selectedEmployeeIds,
    attendanceStatusCodes,
    dimensionalFilter.isExpanded,
    handleAdvancedSearch,
  ]);

  // Selección "todos los X registros" (mismo patrón que ubicaciones): el checkbox del header
  // pasa por 3 estados — página seleccionada → seleccionar el total → ninguno.
  const [isSelectAllTotalActive, setIsSelectAllTotalActive] = useState(false);
  const [selectionVersion, setSelectionVersion] = useState(0);

  useEffect(() => {
    setIsSelectAllTotalActive(false);
  }, [page, pageSize]);

  const tableSelectionKey = useMemo(
    () => `${page}-${pageSize}-${selectionVersion}`,
    [page, pageSize, selectionVersion],
  );

  const onSelectAllTotal = useCallback(() => {
    setIsSelectAllTotalActive(true);
  }, []);

  const onClearSelectionTotal = useCallback(() => {
    setIsSelectAllTotalActive(false);
    setSelectionVersion((version) => version + 1);
  }, []);

  // ASIS-5571: botones "Guardar/Recalcular" y "Refrescar". hasPendingChanges es el estado "sucio"
  // genérico — sin productor todavía (ver nota en UseMarksManagerResult) — que gobierna si
  // "Refrescar" descarta en caliente o pide confirmación.
  const [hasPendingChanges, setHasPendingChanges] = useState(false);
  const [isRecalculating, setIsRecalculating] = useState(false);

  // ASIS-5572: filas agregadas localmente por "+"/"-" en la columna Jornada, agrupadas por la fila
  // ancla (employeeId+date) sobre la que se agregaron. Viven solo en memoria — "Guardar/Recalcular"
  // (ASIS-5571) todavía no las persiste; una historia futura definirá ese contrato. Primer
  // productor real de hasPendingChanges (ver nota arriba).
  const [draftRowsByAnchor, setDraftRowsByAnchor] =
    useState<Record<string, MarksManagerDraftRow[]>>({});
  const draftIdCounter = useRef(0);

  const onAddDayBefore = useCallback((row: MarksManagerRow) => {
    const anchorRowKey = anchorRowKeyOf(row);

    setDraftRowsByAnchor((previous) => {
      const existingBefore = (previous[anchorRowKey] ?? []).filter((draft) => draft.direction === 'before');
      // Cada "-" adicional extiende el rango un día más hacia atrás desde la fila "before" más
      // antigua ya agregada (no siempre un día antes del ancla) — así N clicks producen N días
      // consecutivos en vez de N filas con la misma fecha.
      const earliestBeforeDate = existingBefore.length > 0 ? existingBefore[0].date : row.date;
      const newDraft: MarksManagerDraftRow = {
        employeeId: row.employeeId,
        rut: row.rut,
        fullName: row.fullName,
        date: addDaysToDdMmYyyy(earliestBeforeDate, -1),
        plannedHours: null,
        workedHours: null,
        attendanceStatusCode: null,
        hasOvertimeHours: false,
        hasAbsentHours: false,
        isDraft: true,
        draftId: `draft-${draftIdCounter.current++}`,
        anchorRowKey,
        direction: 'before',
      };

      return {
        ...previous,
        [anchorRowKey]: [newDraft, ...existingBefore, ...(previous[anchorRowKey] ?? []).filter((draft) => draft.direction === 'after')],
      };
    });

    setHasPendingChanges(true);
  }, []);

  const onAddDayAfter = useCallback((row: MarksManagerRow) => {
    const anchorRowKey = anchorRowKeyOf(row);

    setDraftRowsByAnchor((previous) => {
      const existingAfter = (previous[anchorRowKey] ?? []).filter((draft) => draft.direction === 'after');
      const latestAfterDate = existingAfter.length > 0
        ? existingAfter[existingAfter.length - 1].date
        : row.date;
      const newDraft: MarksManagerDraftRow = {
        employeeId: row.employeeId,
        rut: row.rut,
        fullName: row.fullName,
        date: addDaysToDdMmYyyy(latestAfterDate, 1),
        plannedHours: null,
        workedHours: null,
        attendanceStatusCode: null,
        hasOvertimeHours: false,
        hasAbsentHours: false,
        isDraft: true,
        draftId: `draft-${draftIdCounter.current++}`,
        anchorRowKey,
        direction: 'after',
      };

      return {
        ...previous,
        [anchorRowKey]: [...(previous[anchorRowKey] ?? []).filter((draft) => draft.direction === 'before'), ...existingAfter, newDraft],
      };
    });

    setHasPendingChanges(true);
  }, []);

  const onRemoveDraftRow = useCallback((anchorRowKey: string, draftId: string) => {
    setDraftRowsByAnchor((previous) => ({
      ...previous,
      [anchorRowKey]: (previous[anchorRowKey] ?? []).filter((draft) => draft.draftId !== draftId),
    }));
  }, []);

  // ASIS-5572: `rows` cambia de referencia cada vez que usePaginatedList trae una nueva página
  // (paginación, orden, búsqueda o "Buscar" avanzado) — a diferencia de "Guardar/Recalcular" y la
  // confirmación de "Refrescar", esos cambios no pasaban por ningún descarte explícito. Sin este
  // efecto, un draft cuya fila ancla ya no está en la página/filtro actual queda huérfano en
  // draftRowsByAnchor (invisible, pero sin liberar) y hasPendingChanges puede quedar en true sin
  // ninguna fila pendiente visible en la tabla.
  useEffect(() => {
    setDraftRowsByAnchor((previous) => (Object.keys(previous).length === 0 ? previous : {}));
    setHasPendingChanges(false);
  }, [rows]);

  const onSaveRecalculate = useCallback(() => {
    setIsRecalculating(true);

    const filters = lastAppliedFiltersRef.current;
    const recalculateParams: LC_RecalculateMarksManagerIn = {
      dateFrom: filters.dateRange.from ?? undefined,
      dateTo: filters.dateRange.to ?? undefined,
      companyIds: toCsv(filters.companyIds),
      areaIds: toCsv(filters.areaIds),
      positionIds: toCsv(filters.positionIds),
      costCenterIds: toCsv(filters.costCenterIds),
      contractModalityIds: toCsv(filters.contractModalityIds),
      groupIds: toCsv(filters.groupIds),
      employeeStatus: filters.employeeStatus || undefined,
      employeeIds: toCsv(filters.employeeIds),
    };

    void marksManagerService.recalculate(recalculateParams)
      .then((response) => {
        if (!response.success || !response.data) {
          showApiError(response, translate('attendance:marksManager.messages.recalculateError'));
          return;
        }

        // ASIS-5572: "Guardar/Recalcular" no persiste todavía las jornadas agregadas por "+"/"-"
        // (ver nota en draftRowsByAnchor) — se descartan aquí hasta que una historia futura defina
        // ese contrato.
        setDraftRowsByAnchor({});
        setHasPendingChanges(false);
        showSuccess(translate('attendance:marksManager.messages.recalculateSuccess'));
        refetch();
      })
      .finally(() => {
        setIsRecalculating(false);
      });
  }, [showApiError, showSuccess, translate, refetch]);

  const onRefresh = useCallback(() => {
    if (!hasPendingChanges) {
      refetch();
      return;
    }

    openModal('ConfirmModal', {
      variant: 'alert',
      icon: 'alert',
      title: translate('common:unsavedChanges.title'),
      bodyText: translate('common:unsavedChanges.message'),
      confirmText: translate('common:unsavedChanges.confirmLeave'),
      cancelText: translate('common:unsavedChanges.stayOnPage'),
      onConfirm: () => {
        closeModal('ConfirmModal');
        setDraftRowsByAnchor({});
        setHasPendingChanges(false);
        refetch();
      },
      onCancel: () => {
        closeModal('ConfirmModal');
      },
    });
  }, [hasPendingChanges, openModal, closeModal, translate, refetch]);

  // ASIS-5568: switch "Horas decimales" — alterna el formato de las columnas de horas
  // calculadas entre HH:MM:SS y decimal (4 decimales, coma). Mismo patrón que
  // useOvertimeApproval (hourFormat local, sin persistencia entre sesiones).
  const [hourFormat, setHourFormat] = useState<MarksManagerHourFormat>('HH:MM:SS');

  const onHourFormatToggle = useCallback((useDecimal: boolean) => {
    setHourFormat(useDecimal ? 'DECIMAL' : 'HH:MM:SS');
  }, []);

  // Columnas de horario, marcajes y horas de ausencia/justificadas/extras del mockup pertenecen a
  // historias posteriores de la épica (ASIS-5569+) — se dejan como celdas vacías hasta que existan
  // datos/funcionalidad. HHPP/HHTT sí tienen datos reales (caHistoricoLibroAsistenciaDet).
  const emptyCell = useCallback((): null => null, []);

  const renderHours = useCallback((hours: number | null): string | null => {
    if (typeof hours !== 'number') return null;
    return hourFormat === 'DECIMAL'
      ? hours.toFixed(DECIMAL_PLACES).replace('.', ',')
      : convertDecimalToTime(hours);
  }, [hourFormat]);

  // ASIS-5572: "-" agrega una jornada el día anterior a la fila original (ancla); "+" la agrega el
  // día siguiente. Ambos íconos solo se muestran en filas originales (no en drafts) y con permiso
  // de escritura — la fila ancla en sí nunca se modifica (AC de ASIS-5572).
  const renderJornadaCell = useCallback((row: MarksManagerDisplayRow) => {
    const isDraft = 'isDraft' in row && row.isDraft;

    return React.createElement(MarksManagerJornadaCell, {
      date: row.date,
      hasOvertimeHours: row.hasOvertimeHours,
      hasAbsentHours: row.hasAbsentHours,
      isDraft,
      onAddBefore: () => onAddDayBefore(row as MarksManagerRow),
      onAddAfter: () => onAddDayAfter(row as MarksManagerRow),
      onRemoveDraft: () => {
        if (!isDraft) return;
        const draftRow = row as MarksManagerDraftRow;
        onRemoveDraftRow(draftRow.anchorRowKey, draftRow.draftId);
      },
    });
  }, [onAddDayBefore, onAddDayAfter, onRemoveDraftRow]);

  const columns: TableColumn<MarksManagerDisplayRow>[] = useMemo(() => [
    {
      key: 'rut',
      header: translate('attendance:marksManager.columns.rut'),
      width: 100,
      sortable: true,
    },
    {
      key: 'fullName',
      header: translate('attendance:marksManager.columns.employee'),
      width: 200,
      sortable: true,
    },
    {
      key: 'date',
      header: translate('attendance:marksManager.columns.date'),
      width: 195,
      sortable: true,
      render: renderJornadaCell,
    },
    {
      key: 'schedule',
      header: translate('attendance:marksManager.columns.schedule'),
      render: emptyCell,
    },
    {
      key: 'shiftMarks',
      header: translate('attendance:marksManager.columns.shiftMarks'),
      width: 260,
      render: emptyCell,
    },
    {
      key: 'hhpp',
      header: translate('attendance:marksManager.columns.hhpp'),
      width: 80,
      render: (row: MarksManagerDisplayRow) => renderHours(row.plannedHours),
    },
    {
      key: 'hhtt',
      header: translate('attendance:marksManager.columns.hhtt'),
      width: 80,
      render: (row: MarksManagerDisplayRow) => renderHours(row.workedHours),
    },
    {
      key: 'hhaa',
      header: translate('attendance:marksManager.columns.hhaa'),
      align: 'center',
      render: emptyCell,
    },
    {
      key: 'hhjj',
      header: translate('attendance:marksManager.columns.hhjj'),
      render: emptyCell,
    },
    {
      key: 'hhee',
      header: translate('attendance:marksManager.columns.hhee'),
      align: 'center',
      render: emptyCell,
    },
  ], [translate, emptyCell, renderHours, renderJornadaCell]);

  // ASIS-5572: intercala las filas agregadas localmente (draftRowsByAnchor) alrededor de su fila
  // ancla, en el mismo orden que consume Table — los "before" quedan arriba, en orden cronológico
  // (el más antiguo primero), y los "after" abajo (el más reciente al final).
  const displayRows: MarksManagerDisplayRow[] = useMemo(() => {
    if (Object.keys(draftRowsByAnchor).length === 0) return rows;

    return rows.flatMap((row) => {
      const drafts = draftRowsByAnchor[anchorRowKeyOf(row)] ?? [];
      const before = drafts.filter((draft) => draft.direction === 'before');
      const after = drafts.filter((draft) => draft.direction === 'after');
      return [...before, row, ...after];
    });
  }, [rows, draftRowsByAnchor]);

  return {
    rows: displayRows,
    total,
    page,
    pageSize,
    setPage,
    setPageSize,
    columns,
    onSearch,
    onSortChange,
    isLoading: loading,
    onRefresh,
    dateRange: dateRangeDraft,
    setDateRange: setDateRangeDraft,
    queryMode,
    setQueryMode,
    canView,
    isLoaded,
    tableSelectionKey,
    isSelectAllTotalActive,
    onSelectAllTotal,
    onClearSelectionTotal,
    isDecimalFormat: hourFormat === 'DECIMAL',
    onHourFormatToggle,
    dimensionalFilter,
    attendanceStatusCodes,
    setAttendanceStatusCodes,
    onAdvancedSearch: handleAdvancedSearch,
    hasPendingChanges,
    setHasPendingChanges,
    isRecalculating,
    onSaveRecalculate,
  };
}
